import type { App } from '@grafikr/components';
import { v5 as uuidv5 } from 'uuid';

import clear from 'utils/clear';

import fetchText from './fetch-text';
import { NonNullish } from './nullish-check';
import stringMemory from './string-memory';

const INPUT_VALUE_SELECTOR = '[data-filter-input]';

type countsData = {
  [key: string]: number;
}[];

function watch () {
  document.body.addEventListener('scroll', () => {
    sessionStorage.setItem('SCROLL_POSITION', document.body.scrollTop.toString())
  });
}

class Filter {
  private readonly container: HTMLElement;
  private readonly productsWrapper: HTMLElement;
  private readonly mode: 'collection' | 'search';
  private readonly app: App;
  private inputs: NodeListOf<HTMLInputElement | HTMLSelectElement>;
  private resetFilterButtons: NodeListOf<HTMLElement>;
  private productsCount: NodeListOf<HTMLElement>;

  constructor(
    element: string | HTMLElement,
    app: App,
    mode: 'collection' | 'search' = 'collection',
  ) {
    this.app = app;
    this.container =
      typeof element === 'string'
        ? NonNullish(document.querySelector(element))
        : NonNullish(element);
    this.productsWrapper = NonNullish(this.container.querySelector('[data-grid]'));
    this.inputs = document.querySelectorAll(INPUT_VALUE_SELECTOR);

    this.resetFilterButtons = this.container.querySelectorAll('[data-filter-clear]');
    this.productsCount = document.querySelectorAll('[data-products-count]');

    this.mode = mode;

    this.addEventListeners();
  }

  private selectWidth (item: HTMLSelectElement) {
    const dummy = document.createElement('span');
    dummy.textContent = item.options[item.selectedIndex]!.textContent;
    document.body.appendChild(dummy);
    item.style.width = `${dummy.offsetWidth + 20}px`;

    document.body.removeChild(dummy);
  }

  private async addEventListeners() {
    this.inputs.forEach((item) => {
      item.addEventListener('change', this.checkActive.bind(this));

      if (item.name === "sort_by" && 'selectedIndex' in item) {
        this.selectWidth(item)
      }
    });

    const checkedInputs = Array.from(this.inputs).filter((i) => 'checked' in i && i.checked);

    this.resetFilterButtons.forEach((item) => {
      if (!item.hasAttribute('data-no-hide')) item.hidden = checkedInputs.length === 0;
      item.addEventListener('click', this.removeAllFilters.bind(this));
    });

    if (!location.search.includes('filter.v.availability=1') && this.mode !== 'search') {
      this.checkActive();
    } else {
      if (this.mode !== 'search') {
        await this.unavailableProducts(true);
      }
      this.container.classList.remove('loading');
    }

    watch();
  }

  async unavailableProducts(initial?: boolean) {
    const getUnavailableProducts = async () => {
      const query = new URLSearchParams(location.search);
      query.set('filter.v.availability', '0');
      query.set('page', '1');
      query.set('view', 'unavailable');

      const url = `${location.pathname}?${query.toString()}`;
      const uuid = uuidv5(url, uuidv5.URL);

      const memory = stringMemory(sessionStorage);
      return await memory.get(`collection-unavailable-${uuid}`, () => fetchText(url));
    };

    const unavailableProducts = await getUnavailableProducts();

    const totalCounts = document.querySelectorAll('[data-total-count]');
    const pagination = NonNullish(this.container.querySelector<HTMLElement>('nav.pagination'));

    totalCounts.forEach((element) => {
      element.textContent = (+NonNullish(pagination.dataset.total) + (unavailableProducts.match(/product-item/g) || []).length).toString();
    });

    if (pagination.hasAttribute('data-is-last')) {
      this.productsWrapper.insertAdjacentHTML('beforeend', unavailableProducts);
    }

    if (initial) {
      if (sessionStorage.getItem('SCROLL_POSITION') && sessionStorage.getItem('referrer')) {
        document.body.scrollTop = +sessionStorage.getItem('SCROLL_POSITION')!;
        sessionStorage.removeItem('referrer');
      }
    }
  }

  private removeAllFilters() {
    this.inputs.forEach((input) => {
      if (!input.name.includes('sort_by') && 'checked' in input) input.checked = false;
    });

    this.checkActive();
  }

  private async checkActive() {
    const searchParams = new URLSearchParams(window.location.search);
    this.inputs = document.querySelectorAll(INPUT_VALUE_SELECTOR);
    const formData = new FormData();
    const checkedInputs = Array.from(this.inputs).filter((i) => 'checked' in i && i.checked);
    const sortByInput = NonNullish(Array.from(this.inputs).find((i) => i.name === 'sort_by'));

    this.selectWidth(sortByInput as HTMLSelectElement)

    formData.set('sort_by', sortByInput.value);
    if (this.mode !== 'search') {
      formData.set('filter.v.availability', '1');
    }

    checkedInputs.forEach((input) => {
      formData.append(input.name, input.value);
    });

    this.resetFilterButtons.forEach((item) => {
      if (!item.hasAttribute('data-no-hide')) item.hidden = checkedInputs.length === 0;
    });

    if (this.mode === 'search' && window.location.search.length > 1) {
      formData.set('type', 'product');
      formData.set('q', searchParams.get('q') || '');
    }

    const queryString = new URLSearchParams(formData as unknown as string);

    await this.fetchProducts(`?${queryString.toString()}`);
    this.container.classList.remove('loading');
  }

  private async fetchProducts(query: string) {
    const queryString = new URLSearchParams(query);
    queryString.delete('page');

    window.history.replaceState(null, '', query ? `?${queryString.toString()}` : '');

    queryString.set('view', 'filter');

    const url = `${location.pathname}?${queryString.toString()}`;
    const uuid = uuidv5(url, uuidv5.URL);

    const memory = stringMemory(sessionStorage);
    const response = await memory.get(`filter-${uuid}`, () => fetchText(url));
    await this.renderProducts(response);
  }

  async renderProducts(response: string) {
    clear(this.productsWrapper);

    const resultHTML = new DOMParser().parseFromString(response, 'text/html');

    const productItems = NonNullish(resultHTML.querySelector<HTMLElement>('[data-products]'));
    const newPagination = resultHTML.querySelector<HTMLElement>('nav.pagination');
    const pagination = this.container.querySelector<HTMLElement>('nav.pagination');

    const viewingProducts = NonNullish(resultHTML.querySelector<HTMLElement>('[data-count]'));
    const filterCounts: countsData = JSON.parse(
      NonNullish(resultHTML.querySelector<HTMLElement>('[data-filter-counts]')).innerHTML,
    );

    this.productsWrapper.insertAdjacentHTML('beforeend', productItems.innerHTML);
    this.productsWrapper.setAttribute('data-page', '1');
    this.productsCount.forEach((element) => {
      element.innerHTML = NonNullish(viewingProducts).innerHTML;
    });

    if (pagination && newPagination) {
      pagination.replaceWith(newPagination)
    }

    filterCounts.forEach((filter) => {
      const key = Object.keys(filter)[0]!;
      const filterCountElm = document.querySelector<HTMLElement>(`[data-count="${key}"]`);
      if (filterCountElm) filterCountElm.textContent = `(${filter[key]})`;
    });

    if (this.mode !== 'search') {
      await this.unavailableProducts();
      if (sessionStorage.getItem('SCROLL_POSITION') && sessionStorage.getItem('referrer')) {
        document.body.scrollTop = +sessionStorage.getItem('SCROLL_POSITION')!;
        sessionStorage.removeItem('referrer');
      }
    }

    this.app.mount();
  }
}

export default (
  wrapper: string | HTMLElement,
  app: App,
  mode: 'collection' | 'search' = 'collection',
) => new Filter(wrapper, app, mode);
